import {ChangeEvent, MouseEvent, createRef} from "react";

import TextInput from "./TextInput.js";
import SelectInput from "./SelectInput.js";
import NextButton from "./NextButton.js";
import withStateSubmit from "./withStateSubmit.js";
import { MIN_GAMBLING_AGE } from "../constants.js";

import {
  validPhone,
  idBoundValidator,
  yearValidator,
  boundedValidator,
  indexValidator,
  validGender,
  validInputLength,
} from "../validation.js";

import { monthToDay, monthName, filterName, trns } from "../helper.js";
import {
  StringMap,
  SecondStateSubmit,
  SecondState,
  Target,
  KeyboardEvent,
} from "../types/types.js";
import { Segment, Item, Heading, Button, Form } from "ui-components";
import useRegStore from "../store/index.js";

type Props = {
  handleField: (field: string, v: Target | ChangeEvent<HTMLInputElement>) => void;
  state: SecondState;
  onSubmit: (
    e: KeyboardEvent | MouseEvent<HTMLButtonElement>,
    v: StringMap<string>
  ) => Promise<void>;
  reqOrOpt: (field: string) => typeof field;
  isReq: (field: string) => typeof field;
  previousPage: () => void;
  submitted: boolean;
  dirty: StringMap<boolean>;
  isFinal: boolean;
  isStart: boolean;

  // Refs
  user_title_id_ref: typeof createRef;
  fname_ref: typeof createRef;
  lname_ref: typeof createRef;
  sex_ref: typeof createRef;
  birth_date_ref: typeof createRef;
  home_phone_ref: typeof createRef;
  work_phone_ref: typeof createRef;
  cell_phone_ref: typeof createRef;
  year_ref: typeof createRef;
  month_ref: typeof createRef;
  day_ref: typeof createRef;
};

const state: SecondState = {
  user_title_id: "",
  fname: "",
  lname: "",
  sex: "",
  birth_date: "",
  home_phone: "",
  work_phone: "",
  cell_phone: "",
  year: "",
  month: "",
  day: "",
};

const Second = ({
  previousPage,
  handleField,
  state,
  onSubmit,
  reqOrOpt,
  isReq,
  dirty,
  isFinal,
  submitted,
  isStart,
  user_title_id_ref,
  fname_ref,
  lname_ref,
  sex_ref,
  home_phone_ref,
  work_phone_ref,
  cell_phone_ref,
  year_ref,
  month_ref,
  day_ref,
}: Props) => {
  const title_ids = useRegStore(state => state.title_ids);
  const currentYear: number = new Date().getFullYear() - 1900;
  const months = monthToDay(currentYear);
  const monthIds = Object.keys(months);
  const selectedMonth: number = Number(state.month);
  const selectedYear: number = Number(state.year);
  const totalDays = monthToDay(selectedYear)[selectedMonth] || 31;
  const dayIds = new Array(totalDays).fill(0);
  const additionalFields = reqOrOpt("user_title_id") || reqOrOpt("sex");

  return (
    <Form className="fieldset">
      <Heading headingLevel="h4">
        {trns("IDS_REGISTRATION_ACCOUNT_INFORMATION", "Account Information")}
      </Heading>

      <Segment className="form-groups">
        {additionalFields && (
          <Segment className="form-group form-group-clear">
            {reqOrOpt("user_title_id") && (
              <SelectInput
                placeholder={trns("IDS_SELECT_TITLE", "Select Title")}
                label={trns("IDS_LOGINBOX_TITLE_PROMPT", "Title:")}
                dirty={dirty.user_title_id}
                onInput={v => handleField("user_title_id", v)}
                value={state.user_title_id}
                validator={value => idBoundValidator(title_ids, value)}
                req={false}
                ref={user_title_id_ref}
                options={title_ids}
              />
            )}

            {reqOrOpt("sex") && (
              <SelectInput
                placeholder={trns("IDS_SELECT_GENDER", "Select Gender")}
                label={trns("IDS_LOGINBOX_GENDER", "Gender:")}
                dirty={dirty.sex}
                onInput={v => handleField("sex", v)}
                validator={validGender}
                value={state.sex}
                req={isReq("sex")}
                ref={sex_ref}
                options={[
                  {
                    id: "male",
                    value: "male",
                    name: trns("IDS_LOGINBOX_GENDER_MALE", "Male"),
                  },
                  {
                    id: "female",
                    value: "female",
                    name: trns("IDS_LOGINBOX_GENDER_FEMALE", "Female"),
                  },
                ]}
              />
            )}
          </Segment>
        )}

        {reqOrOpt("fname") && (
          <TextInput
            placeholder={trns("IDS_LOGINBOX_FIRSTNAME", "First name")}
            label={trns("IDS_LOGINBOX_FIRSTNAME_PROMPT", "First name:")}
            req={isReq("fname")}
            onInput={v => handleField("fname", filterName(v))}
            value={state.fname}
            dirty={dirty.fname}
            validator={validInputLength}
            ref={fname_ref}
          />
        )}

        {reqOrOpt("lname") && (
          <TextInput
            placeholder={trns("IDS_LOGINBOX_LASTNAME", "Last name")}
            label={trns("IDS_LOGINBOX_LASTNAME_PROMPT", "Last name:")}
            req={isReq("lname")}
            onInput={v => handleField("lname", filterName(v))}
            value={state.lname}
            dirty={dirty.lname}
            validator={validInputLength}
            ref={lname_ref}
          />
        )}

        {(reqOrOpt("year") || reqOrOpt("month") || reqOrOpt("day")) && (
          <Segment className="form-group select-group">
            <span className="label control-label">
              {trns("IDS_LOGINBOX_BIRTH_DATE_PROMPT", "Birth date:")} *
            </span>

            {reqOrOpt("year") && (
              <SelectInput
                placeholder={trns("IDS_DATE_YEAR", "Year")}
                className="select-birth-date"
                onInput={v => handleField("year", v)}
                value={state.year}
                dirty={dirty.year}
                req={isReq("year")}
                validator={yearValidator}
                ref={year_ref}
                options={new Array(currentYear).fill(1).map((y, i) => ({
                  id: 1900 + currentYear - (i + MIN_GAMBLING_AGE),
                  value: 1900 + currentYear - (i + MIN_GAMBLING_AGE),
                  name: 1900 + currentYear - (i + MIN_GAMBLING_AGE),
                }))}
              />
            )}

            {reqOrOpt("month") && (
              <SelectInput
                className="select-birth-date"
                placeholder={trns("IDS_DATE_MONTH", "Month")}
                onInput={v => handleField("month", v)}
                value={state.month}
                dirty={dirty.month}
                req={isReq("month")}
                // @ts-expect-error
                validator={value => boundedValidator(monthIds, value)}
                ref={month_ref}
                options={monthIds.map((m, i) => ({
                  id: m,
                  value: m,
                  name: monthName(m),
                }))}
              />
            )}

            {reqOrOpt("day") && (
              <SelectInput
                className="select-birth-date"
                placeholder={trns("IDS_DATE_DAY", "Day")}
                onInput={v => handleField("day", v)}
                value={state.day}
                dirty={dirty.day}
                req={isReq("day")}
                validator={value => indexValidator(dayIds, value)}
                ref={day_ref}
                options={dayIds.map((d, i) => ({
                  id: i + 1,
                  value: i + 1,
                  name: i + 1,
                }))}
              />
            )}
          </Segment>
        )}

        {reqOrOpt("work_phone") && (
          <TextInput
            placeholder={trns("IDS_WORK_PHONE", "Work Phone")}
            label={trns("IDS_WORK_PHONE_PROMPT", "Work Phone:")}
            type="tel"
            ref={work_phone_ref}
            req={isReq("work_phone")}
            onInput={v => {
              let field = v.target.value.split("");
              handleField("work_phone", {
                target: {
                  value: field
                    .filter((c: string) => parseInt(c, 10) >= 0 && parseInt(c, 10) <= 9)
                    .join(""),
                },
              });
            }}
            value={state.work_phone}
            dirty={dirty.work_phone}
            validator={validPhone}
          />
        )}

        {reqOrOpt("cell_phone") && (
          <Segment className="form-group form-mobile-num">
            <TextInput
              placeholder={trns("IDS_MOBILE_PHONE", "Mobile Phone")}
              label={trns("IDS_MOBILE_PHONE_PROMPT", "Mobile Phone:")}
              type="tel"
              ref={cell_phone_ref}
              req={isReq("cell_phone")}
              onInput={v => {
                let field = v.target.value.split("");
                handleField("cell_phone", {
                  target: {
                    value: field
                      .filter((c: string) => parseInt(c, 10) >= 0 && parseInt(c, 10) <= 9)
                      .join(""),
                  },
                });
              }}
              value={state.cell_phone}
              dirty={dirty.cell_phone}
              validator={validPhone}
            />

            <p className="helper-text">
              {trns("IDS_SMS_COUNTRY_CODE", "Include your country and area code")}
              <span>
                {trns("IDS_SMS_COUNTRY_CODE_EXAMPLE_EU", "e.g. International: 442071838750")}
                <br />
                {trns("IDS_SMS_COUNTRY_CODE_EXAMPLE_NA", "e.g. North America: 1415555267")}
              </span>
            </p>
          </Segment>
        )}

        {reqOrOpt("home_phone") && (
          <TextInput
            placeholder={trns("IDS_HOME_PHONE", "Home Phone")}
            label={trns("IDS_HOME_PHONE_PROMPT", "Home Phone:")}
            type="tel"
            req={isReq("home_phone")}
            ref={home_phone_ref}
            onInput={v => {
              let field = v.target.value.split("");
              handleField("home_phone", {
                target: {
                  value: field
                    .filter((c: string) => parseInt(c, 10) >= 0 && parseInt(c, 10) <= 9)
                    .join(""),
                },
              });
            }}
            value={state.home_phone}
            dirty={dirty.home_phone}
            validator={validPhone}
          />
        )}
      </Segment>

      <Segment className="btn-toolbar reg-nav x">
        <Item className="grid grid-6">
          <Button
            className="btn"
            disabled={isStart}
            onClick={() => {
              !isStart && previousPage();
            }}
          />
        </Item>

        <Item className="grid grid-6">
          <NextButton
            onSubmit={e => {
              let birth = {};
              if (dirty.year && dirty.day && dirty.month) {
                birth = { birth_date: `${state.year}-${state.month}-${state.day}` };
              }
              return onSubmit(e, birth);
            }}
            disabled={submitted}
            isStart={isStart}
            isFinal={isFinal}
          />
        </Item>
      </Segment>
    </Form>
  );
};

export default withStateSubmit(Second, state, "step2", (t: SecondStateSubmit) => ({
  day_ref: (el: typeof createRef) => {
    t.day_ref = el;
  },
  month_ref: (el: typeof createRef) => {
    t.month_ref = el;
  },
  year_ref: (el: typeof createRef) => {
    t.year_ref = el;
  },
  user_title_id_ref: (el: typeof createRef) => {
    t.user_title_id_ref = el;
  },
  fname_ref: (el: typeof createRef) => {
    t.fname_ref = el;
  },
  lname_ref: (el: typeof createRef) => {
    t.lname_ref = el;
  },
  home_phone_ref: (el: typeof createRef) => {
    t.home_phone_ref = el;
  },
  work_phone_ref: (el: typeof createRef) => {
    t.work_phone_ref = el;
  },
  cell_phone_ref: (el: typeof createRef) => {
    t.cell_phone_ref = el;
  },
  sex_ref: (el: typeof createRef) => {
    t.sex_ref = el;
  },
}));
